import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ReactECharts from "echarts-for-react";

import { listSites } from "../../api/sites";
import {
  createIecPrRun,
  downloadIecPrCsv,
  getIecPrConfigPreview,
  getIecPrExclusions,
  getIecPrMinutes,
  getIecPrRun,
  listIecPrRuns,
  type IecPrExclusionInput,
} from "../../api/iecPrReports";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";

type SiteOption = { id: number; name: string };

type SignalRef = {
  signal_name?: string | null;
  source_name?: string | null;
};

type SensorSnapshot = {
  id?: number | null;
  signal_match_id?: number | null;
  signal_name?: string | null;
  source_name?: string | null;
  source_name_snapshot?: string | null;
  signal_type_short?: string | null;
  sensor_role?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  sensor_serial_number?: string | null;
  asset_tag?: string | null;
  measurement_unit?: string | null;
  accuracy_class?: string | null;
  location_note?: string | null;
  calibration_certificate_number?: string | null;
  calibration_provider?: string | null;
  calibration_date?: string | null;
  calibration_valid_until?: string | null;
  certificate_filename?: string | null;
  certificate_available?: boolean | null;
  certificate_status?: string | null;
  certificate_url?: string | null;
  calibration_status?: string | null;
  notes?: string | null;
};

type ConfigSection = {
  section_key: string;
  section_label: string;
  array_ids: number[];
  array_names?: string[];
  installed_dc_kwp: number;
  installed_ac_kw: number;
  dc_weighting_factor?: number | null;
  ac_weighting_factor?: number | null;
  gamma: number;
  irradiance_signal_source?: SignalRef;
  module_temp_signal_source?: SignalRef;
  irradiance_sensor_snapshot?: SensorSnapshot | null;
  module_temp_sensor_snapshot?: SensorSnapshot | null;
};

type ConfigPreview = {
  site?: { id: number; name: string; timezone?: string | null };
  report_date?: string | null;
  default_irradiance_threshold_wm2?: number | null;
  sections: ConfigSection[];
  warnings: string[];
  errors: string[];
  poutsite_source?: SignalRef | null;
  setpoint_source?: SignalRef | null;
};

type GraphicSummaryItem = {
  timestamp_minute_utc: string;
  poutsite_power_w?: number | null;
  poa_irradiance_wm2?: number | null;
  module_temp_c?: number | null;
};

type RunPayload = {
  meta?: {
    run_id: number;
    site_name?: string;
    timezone?: string;
    report_date?: string;
    methodology_version?: string;
    irradiance_threshold_wm2?: number | null;
    contract_method_name?: string | null;
    contract_method_notes?: string | null;
    notes?: string | null;
    apply_clipping_correction?: boolean;
    apply_curtailment_correction?: boolean;
    apply_availability_correction?: boolean;
  };
  summary?: {
    pr_iec_raw?: number | null;
    pr_corrected?: number | null;
    total_measured_energy_wh?: number | null;
    total_measured_energy_evaluated_wh?: number | null;
    total_full_day_poi_energy_wh?: number | null;
    total_expected_iec_wh?: number | null;
    total_expected_corrected_wh?: number | null;
    total_clipping_loss_wh?: number | null;
    total_curtailment_loss_wh?: number | null;
    total_availability_loss_wh?: number | null;
    valid_poi_minutes?: number | null;
    valid_section_rows?: number | null;
    invalid_poi_minutes?: number | null;
    invalid_section_rows?: number | null;
    evaluated_minutes?: number | null;
    grid_availability_observed_pct?: number | null;
    availability_correction_enabled?: boolean | null;
    availability_avg_pct_evaluated_minutes?: number | null;
    below_irradiance_threshold_minutes?: number | null;
    excluded_minutes_by_reason?: Record<string, number>;
    stale_filled_minutes_by_signal?: Record<string, number>;
  };
  main_results?: {
    grid_availability_observed_pct?: number | null;
    pure_iec_pr?: number | null;
    corrected_pr?: number | null;
    availability_avg_pct_evaluated_minutes?: number | null;
  };
  sections?: Array<{
    section_key: string;
    section_label: string;
    array_ids: number[];
    array_names?: string[];
    installed_dc_kwp?: number | null;
    installed_ac_kw?: number | null;
    dc_weighting_factor?: number | null;
    ac_weighting_factor?: number | null;
    gamma?: number | null;
    irradiance_sensor_snapshot?: SensorSnapshot | null;
    module_temp_sensor_snapshot?: SensorSnapshot | null;
    expected_iec_wh?: number | null;
    expected_corrected_wh?: number | null;
    clipping_loss_wh?: number | null;
    curtailment_loss_wh?: number | null;
    availability_loss_wh?: number | null;
    availability_avg_pct?: number | null;
    valid_minutes?: number | null;
  }>;
  signals?: {
    poutsite?: SignalRef | null;
    setpoint?: SignalRef | null;
  };
  warnings?: string[];
  polling?: Record<string, number>;
  notes_block?: string[];
  instrumentation?: Array<
    SensorSnapshot & {
      section_key?: string | null;
      section_label?: string | null;
    }
  >;
  graphic_summary?: {
    items: GraphicSummaryItem[];
  };
};

type RunResponse = {
  id: number;
  site_id: number;
  report_date: string;
  timezone: string;
  status: string;
  run_type: string;
  payload?: RunPayload | null;
  error_message?: string | null;
};

type MinuteResponse = {
  total: number;
  items: Array<Record<string, string | number | boolean | null>>;
};

type ExistingRun = {
  id: number;
  site_id: number;
  report_date: string;
  status: string;
  created_at?: string | null;
  irradiance_threshold_wm2?: number | null;
  apply_clipping_correction: boolean;
  apply_curtailment_correction: boolean;
  apply_availability_correction: boolean;
  exclusion_count: number;
  run_signature?: string | null;
  pr_iec_raw?: number | null;
  pr_corrected?: number | null;
  total_full_day_poi_energy_wh?: number | null;
  total_measured_energy_evaluated_wh?: number | null;
  availability_avg_pct_evaluated_minutes?: number | null;
};

const DEFAULT_EXCLUSION: IecPrExclusionInput = {
  start_time: "08:30",
  end_time: "09:00",
  exclusion_type: "manual",
  exclusion_scope: "site",
  reason: "",
};

const DEFAULT_REPORT_METHOD =
  "IEC 61724-1:2021 sec. 14.3.2 Temperature-corrected performance ratio";

const DEFAULT_REPORT_NOTE =
  "Pure IEC PR is evaluated on valid minutes above the configured POA irradiance threshold. Corrected PR applies plant AC physical cap, DSO curtailment, and optional inverter availability corrections while retaining the raw IEC result.";

const percent = (value?: number | null, precision = 2) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(precision)}%`;
};

const numberLabel = (value?: number | null, precision = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const energyLabel = (wh?: number | null) => {
  if (wh === null || wh === undefined || Number.isNaN(Number(wh))) return "—";
  return `${numberLabel(Number(wh) / 1000, 1)} kWh`;
};

const mwLabel = (w?: number | null, precision = 3) => {
  if (w === null || w === undefined || Number.isNaN(Number(w))) return "—";
  return `${numberLabel(Number(w) / 1_000_000, precision)} MW`;
};

const toneClass = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "text-slate-500";
  if (Number(value) >= 0.9) return "text-emerald-600";
  if (Number(value) >= 0.75) return "text-amber-600";
  return "text-rose-600";
};

const displaySignal = (signal?: SignalRef | null) => signal?.source_name || signal?.signal_name || "—";
const displaySensorName = (sensor?: SensorSnapshot | null) => sensor?.source_name_snapshot || sensor?.source_name || sensor?.signal_name || "—";
const instrumentStatusTone = (label?: string | null) => {
  switch (label) {
    case "Valid":
    case "Certificate uploaded":
      return "bg-emerald-100 text-emerald-700";
    case "Expired":
      return "bg-rose-100 text-rose-700";
    case "Missing calibration metadata":
    case "Missing certificate file":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
};

const SummaryCard: React.FC<{ label: string; value: string; tone?: string; hint?: string }> = ({ label, value, tone = "text-slate-900", hint }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`mt-2 text-2xl font-semibold ${tone}`}>{value}</div>
    {hint ? <div className="mt-2 text-xs text-slate-500">{hint}</div> : null}
  </div>
);

const SectionBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
    {children}
  </span>
);

const StepButton: React.FC<{ active: boolean; children: React.ReactNode; onClick: () => void }> = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-5 py-3 text-sm font-medium transition ${
      active ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-700 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);

const ArrayNamesBlock: React.FC<{ names?: string[] }> = ({ names }) => {
  const lines = chunkArray(names || [], 4);
  if (!lines.length) return <span className="text-slate-500">—</span>;
  return (
    <div className="space-y-1">
      {lines.map((line, index) => (
        <div key={`array-line-${index}`} className="text-slate-900">
          {line.join(", ")}
        </div>
      ))}
    </div>
  );
};

const InstrumentStatusBadge: React.FC<{ label?: string | null }> = ({ label }) =>
  label ? <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${instrumentStatusTone(label)}`}>{label}</span> : null;

const instrumentationPreviewRows = (sections?: ConfigSection[]) =>
  (sections || []).flatMap((section) => [
    {
      section_label: section.section_label,
      sensor_role: "IRRAD",
      sensor: section.irradiance_sensor_snapshot,
    },
    {
      section_label: section.section_label,
      sensor_role: "MODTEMP",
      sensor: section.module_temp_sensor_snapshot,
    },
  ]);

const buildGraphicSummaryChart = (items: GraphicSummaryItem[]) => {
  const filtered = items.filter(
    (row) =>
      row.poa_irradiance_wm2 !== null ||
      row.module_temp_c !== null ||
      row.poutsite_power_w !== null
  );
  if (!filtered.length) return null;

  return {
    color: ["#ef4444", "#f59e0b", "#2563eb"],
    tooltip: {
      trigger: "axis",
      valueFormatter: (value: number) => (value == null ? "—" : numberLabel(value, 2)),
    },
    legend: {
      top: 0,
      textStyle: { color: "#475569", fontSize: 11 },
    },
    grid: { left: 60, right: 68, top: 42, bottom: 78 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0 },
      { type: "slider", xAxisIndex: 0, height: 18, bottom: 26 },
    ],
    xAxis: {
      type: "category",
      data: filtered.map((row) => row.timestamp_minute_utc),
      axisLabel: {
        color: "#64748b",
        fontSize: 11,
        formatter: (value: string) =>
          new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
      },
      axisLine: { lineStyle: { color: "#cbd5e1" } },
    },
    yAxis: [
      {
        type: "value",
        name: "POA irradiance [W/m2]",
        nameTextStyle: { color: "#64748b", fontSize: 11 },
        axisLabel: { color: "#64748b", fontSize: 11 },
        splitLine: { lineStyle: { color: "#e2e8f0" } },
      },
      {
        type: "value",
        name: "POUTSITE [MW]",
        nameTextStyle: { color: "#64748b", fontSize: 11 },
        axisLabel: {
          color: "#64748b",
          fontSize: 11,
          formatter: (value: number) => numberLabel(value, 2),
        },
        splitLine: { show: false },
      },
      {
        type: "value",
        name: "Module temp [C]",
        nameTextStyle: { color: "#64748b", fontSize: 11 },
        axisLabel: { color: "#64748b", fontSize: 11 },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "POA irradiance [W/m2]",
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { width: 2.5, color: "#ef4444" },
        itemStyle: { color: "#ef4444" },
        data: filtered.map((row) => row.poa_irradiance_wm2),
      },
      {
        name: "POUTSITE [MW]",
        type: "line",
        yAxisIndex: 1,
        smooth: true,
        symbol: "none",
        lineStyle: { width: 2.5, color: "#f59e0b" },
        itemStyle: { color: "#f59e0b" },
        data: filtered.map((row) => (row.poutsite_power_w == null ? null : Number(row.poutsite_power_w) / 1_000_000)),
      },
      {
        name: "Module temp [C]",
        type: "line",
        yAxisIndex: 2,
        smooth: true,
        symbol: "none",
        lineStyle: { width: 2.5, color: "#2563eb" },
        itemStyle: { color: "#2563eb" },
        data: filtered.map((row) => row.module_temp_c),
      },
    ],
  };
};

const GraphicSummaryChart: React.FC<{ items: GraphicSummaryItem[] }> = ({ items }) => {
  const option = React.useMemo(() => buildGraphicSummaryChart(items), [items]);
  if (!option) {
    return <div className="rounded-xl border border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500">No chart data available.</div>;
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 text-sm text-slate-500">Drag to zoom, scroll to inspect, and use the slider to move through the day.</div>
      <ReactECharts option={option as any} style={{ height: 380, width: "100%" }} notMerge lazyUpdate />
    </div>
  );
};

const IecPrReportsPage: React.FC = () => {
  const [sites, setSites] = React.useState<SiteOption[]>([]);
  const [siteId, setSiteId] = React.useState<string>("");
  const [reportDate, setReportDate] = React.useState<string>(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = React.useState<ConfigPreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [runLoading, setRunLoading] = React.useState(false);
  const [csvLoading, setCsvLoading] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [error, setError] = React.useState<string>("");
  const [runResult, setRunResult] = React.useState<RunResponse | null>(null);
  const [existingRuns, setExistingRuns] = React.useState<ExistingRun[]>([]);
  const [exclusionRows, setExclusionRows] = React.useState<IecPrExclusionInput[]>([]);
  const [runExclusions, setRunExclusions] = React.useState<any[]>([]);
  const [minuteRows, setMinuteRows] = React.useState<MinuteResponse | null>(null);
  const [applyClippingCorrection, setApplyClippingCorrection] = React.useState(true);
  const [applyCurtailmentCorrection, setApplyCurtailmentCorrection] = React.useState(true);
  const [applyAvailabilityCorrection, setApplyAvailabilityCorrection] = React.useState(true);
  const [irradianceThreshold, setIrradianceThreshold] = React.useState<number>(200);
  const [reportMethodName, setReportMethodName] = React.useState(DEFAULT_REPORT_METHOD);
  const [reportMethodNotes, setReportMethodNotes] = React.useState(DEFAULT_REPORT_NOTE);
  const [notes, setNotes] = React.useState("");
  const [activeStep, setActiveStep] = React.useState<1 | 2>(1);
  const [duplicateRunId, setDuplicateRunId] = React.useState<number | null>(null);
  const [storedReportsOpen, setStoredReportsOpen] = React.useState(false);
  const [minutePage, setMinutePage] = React.useState(0);
  const [minutePageSize, setMinutePageSize] = React.useState(100);
  const [minuteLoading, setMinuteLoading] = React.useState(false);

  const loadMinuteRows = React.useCallback(async (runId: number | string, page: number, pageSize: number) => {
    setMinuteLoading(true);
    try {
      const minutesData = await getIecPrMinutes(runId, pageSize, page * pageSize);
      setMinuteRows(minutesData);
      setMinutePage(page);
    } finally {
      setMinuteLoading(false);
    }
  }, []);

  const loadRunDetails = React.useCallback(
    async (runId: number | string) => {
      const [runData, exclusionsData] = await Promise.all([
        getIecPrRun(runId),
        getIecPrExclusions(runId),
      ]);
      setRunResult(runData as RunResponse);
      setRunExclusions(exclusionsData || []);
      await loadMinuteRows(runId, 0, minutePageSize);
      setActiveStep(2);
      setStoredReportsOpen(false);
    },
    [loadMinuteRows, minutePageSize]
  );

  const loadHistory = React.useCallback(
    async (selectedSiteId: string) => {
      if (!selectedSiteId) {
        setExistingRuns([]);
        return;
      }
      setHistoryLoading(true);
      try {
        const data = await listIecPrRuns(selectedSiteId);
        setExistingRuns(data?.items || []);
      } catch (loadError: any) {
        setExistingRuns([]);
        setError(loadError?.response?.data?.detail || "Failed to load stored IEC PR runs.");
      } finally {
        setHistoryLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    const loadSites = async () => {
      try {
        const data = await listSites();
        setSites(data || []);
        if (data?.length) {
          setSiteId(String(data[0].id));
        }
      } catch (loadError: any) {
        setError(loadError?.response?.data?.detail || "Failed to load sites.");
      }
    };
    loadSites();
  }, []);

  React.useEffect(() => {
    if (!siteId || !reportDate) return;
    setRunResult(null);
    setRunExclusions([]);
    setMinuteRows(null);
    setDuplicateRunId(null);
    setMinutePage(0);
    const loadPreview = async () => {
      setPreviewLoading(true);
      setError("");
      try {
        const data = await getIecPrConfigPreview(siteId, reportDate);
        setPreview(data);
        if (typeof data?.default_irradiance_threshold_wm2 === "number") {
          setIrradianceThreshold((current) => (current > 0 ? current : data.default_irradiance_threshold_wm2));
        }
      } catch (loadError: any) {
        setPreview(null);
        setError(loadError?.response?.data?.detail || "Failed to load IEC PR configuration preview.");
      } finally {
        setPreviewLoading(false);
      }
    };
    loadPreview();
  }, [siteId, reportDate]);

  const addExclusion = () => setExclusionRows((current) => [...current, { ...DEFAULT_EXCLUSION }]);

  const updateExclusion = (index: number, patch: Partial<IecPrExclusionInput>) => {
    setExclusionRows((current) => current.map((row, idx) => (idx === index ? { ...row, ...patch } : row)));
  };

  const removeExclusion = (index: number) => {
    setExclusionRows((current) => current.filter((_, idx) => idx !== index));
  };

  const runEvaluation = async (forceRecompute = false) => {
    if (!siteId || !reportDate) {
      setError("Select site and report date.");
      return;
    }
    if (preview?.errors?.length) {
      setError("Resolve IEC configuration errors before running the report.");
      return;
    }

    setRunLoading(true);
    setError("");
    setDuplicateRunId(null);
    try {
      const response = (await createIecPrRun({
        site_id: Number(siteId),
        report_date: reportDate,
        apply_clipping_correction: applyClippingCorrection,
        apply_curtailment_correction: applyCurtailmentCorrection,
        apply_availability_correction: applyAvailabilityCorrection,
        irradiance_threshold_wm2: irradianceThreshold,
        contract_method_name: reportMethodName || undefined,
        contract_method_notes: reportMethodNotes || undefined,
        notes: notes || undefined,
        exclusions: exclusionRows,
        force_recompute: forceRecompute,
      })) as RunResponse;
      await loadRunDetails(response.id);
      if (storedReportsOpen) {
        await loadHistory(siteId);
      }
    } catch (runError: any) {
      const detail = runError?.response?.data?.detail;
      if (runError?.response?.status === 409 && detail?.action === "duplicate_run" && detail?.existing_run_id) {
        setDuplicateRunId(Number(detail.existing_run_id));
        setError(detail?.message || "Equivalent IEC PR report run already exists.");
      } else {
        setRunResult(null);
        setMinuteRows(null);
        setRunExclusions([]);
        setError(detail || "IEC PR run failed.");
      }
    } finally {
      setRunLoading(false);
    }
  };

  const openStoredReports = async () => {
    if (!siteId) return;
    setStoredReportsOpen(true);
    await loadHistory(siteId);
  };

  const handleCsvDownload = async () => {
    if (!runResult?.id) return;
    setCsvLoading(true);
    try {
      const response = await downloadIecPrCsv(runResult.id);
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `iec_pr_run_${runResult.id}_${runResult.report_date}.csv`);
    } catch (downloadError: any) {
      setError(downloadError?.response?.data?.detail || "Failed to download IEC raw CSV.");
    } finally {
      setCsvLoading(false);
    }
  };

  const handlePdfDownload = async () => {
    if (!runResult?.payload) return;
    setPdfLoading(true);
    try {
      const payload = runResult.payload;
      const summary = payload.summary || {};
      const sections = payload.sections || [];
      const instrumentation = payload.instrumentation || [];
      const pdf = new jsPDF("p", "pt", "a4");
      const margin = 40;
      let y = margin;

      pdf.setFontSize(18);
      pdf.text("IEC PR On-Demand Report", margin, y);
      y += 22;
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Site: ${payload.meta?.site_name || "—"}`, margin, y);
      y += 14;
      pdf.text(`Date: ${payload.meta?.report_date || "—"}    Run ID: ${payload.meta?.run_id || "—"}`, margin, y);
      y += 14;
      pdf.text(`Timezone: ${payload.meta?.timezone || "—"}    Threshold: ${numberLabel(payload.meta?.irradiance_threshold_wm2, 0)} W/m2`, margin, y);
      y += 18;

      autoTable(pdf, {
        startY: y,
        head: [["Metric", "Value"]],
        body: [
          ["Grid availability observed", percent(payload.main_results?.grid_availability_observed_pct)],
          ["Pure IEC PR", percent(payload.main_results?.pure_iec_pr)],
          ["Corrected PR", percent(payload.main_results?.corrected_pr)],
          ["Full-Day POI Energy", energyLabel(summary.total_full_day_poi_energy_wh ?? summary.total_measured_energy_wh)],
          [
            "Measured Energy (Evaluated Minutes)",
            energyLabel(summary.total_measured_energy_evaluated_wh ?? summary.total_measured_energy_wh),
          ],
          ["Expected Energy IEC", energyLabel(summary.total_expected_iec_wh)],
          ["Expected Energy Corrected", energyLabel(summary.total_expected_corrected_wh)],
          ["Clipping Loss", energyLabel(summary.total_clipping_loss_wh)],
          ["Curtailment Loss", energyLabel(summary.total_curtailment_loss_wh)],
          ["Availability Loss", energyLabel(summary.total_availability_loss_wh)],
          ["Availability on Evaluated Minutes", percent(summary.availability_avg_pct_evaluated_minutes)],
          ["Evaluated minutes", String(summary.evaluated_minutes ?? "—")],
        ],
        theme: "grid",
        headStyles: { fillColor: [15, 23, 42] },
      });

      y = (pdf as any).lastAutoTable.finalY + 18;

      autoTable(pdf, {
        startY: y,
        head: [["Section", "IRRAD", "MODTEMP", "Arrays", "DC kWp", "AC kW", "DC wt", "AC wt"]],
        body: sections.map((section) => [
          (section.array_names || []).join(", "),
          displaySignal((preview?.sections || []).find((row) => row.section_key === section.section_key)?.irradiance_signal_source),
          displaySignal((preview?.sections || []).find((row) => row.section_key === section.section_key)?.module_temp_signal_source),
          String(section.array_ids?.length || 0),
          numberLabel(section.installed_dc_kwp, 1),
          numberLabel(section.installed_ac_kw, 1),
          percent(section.dc_weighting_factor, 2),
          percent(section.ac_weighting_factor, 2),
        ]),
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [30, 41, 59] },
      });

      y = (pdf as any).lastAutoTable.finalY + 18;

      autoTable(pdf, {
        startY: y,
        head: [["Section", "Role", "Sensor", "Make / model", "Serial", "Certificate", "Valid until", "Status"]],
        body: instrumentation.length
          ? instrumentation.map((item) => [
              item.section_label || "—",
              item.sensor_role || item.signal_type_short || "—",
              displaySensorName(item),
              [item.manufacturer, item.model].filter(Boolean).join(" / ") || "—",
              item.sensor_serial_number || "—",
              item.calibration_certificate_number || "—",
              item.calibration_valid_until || "—",
              [item.calibration_status, item.certificate_status].filter(Boolean).join(" | ") || "—",
            ])
          : [["—", "—", "No instrumentation metadata stored for this run", "—", "—", "—", "—", "—"]],
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [37, 99, 235] },
      });

      y = (pdf as any).lastAutoTable.finalY + 18;

      autoTable(pdf, {
        startY: y,
        head: [["Notes"]],
        body: (payload.notes_block?.length ? payload.notes_block : ["No notes"]).map((row) => [row]),
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [51, 65, 85] },
      });

      pdf.save(`iec_pr_report_${runResult.id}_${runResult.report_date}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  };

  const summary = runResult?.payload?.summary;
  const mainResults = runResult?.payload?.main_results;
  const fullDayPoiEnergyWh = summary?.total_full_day_poi_energy_wh ?? summary?.total_measured_energy_wh;
  const measuredEnergyEvaluatedWh = summary?.total_measured_energy_evaluated_wh ?? summary?.total_measured_energy_wh;
  const minuteRowStart = minuteRows?.total ? minutePage * minutePageSize + 1 : 0;
  const minuteRowEnd = minuteRows?.total ? Math.min((minutePage + 1) * minutePageSize, minuteRows.total) : 0;
  const minuteTotalPages = minuteRows?.total ? Math.max(Math.ceil(minuteRows.total / minutePageSize), 1) : 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">IEC PR Reports</h1>
          <p className="mt-1 text-sm text-slate-600">
            Run a separate one-day IEC-compatible PR evaluation without changing the existing scheduled reporting flow.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100"
            onClick={openStoredReports}
            disabled={!siteId}
          >
            Stored reports
          </button>
          <button
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            onClick={() => runEvaluation(false)}
            disabled={runLoading || previewLoading || !siteId || !reportDate || Boolean(preview?.errors?.length)}
          >
            {runLoading ? "Running..." : "Run IEC PR"}
          </button>
          <button
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100"
            onClick={handlePdfDownload}
            disabled={pdfLoading || !runResult?.payload}
          >
            {pdfLoading ? "Preparing..." : "Print report"}
          </button>
          <button
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100"
            onClick={handleCsvDownload}
            disabled={csvLoading || !runResult?.id}
          >
            {csvLoading ? "Preparing..." : "Export raw data"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <StepButton active={activeStep === 1} onClick={() => setActiveStep(1)}>
            Step 1 - Settings
          </StepButton>
          <StepButton active={activeStep === 2} onClick={() => setActiveStep(2)}>
            Step 2 - Report
          </StepButton>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
      ) : null}

      {duplicateRunId ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
          <div className="text-sm font-semibold text-amber-900">Equivalent report already exists</div>
          <p className="mt-2 text-sm text-amber-800">
            A report for the same site, day, threshold, correction settings, and exclusion list already exists.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
              onClick={() => loadRunDetails(duplicateRunId)}
            >
              Open existing
            </button>
            <button
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"
              onClick={() => runEvaluation(true)}
            >
              Recompute and overwrite
            </button>
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
              onClick={() => setDuplicateRunId(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {activeStep === 1 ? (
        <>
          <div className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Report settings</h2>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm">
                  <span className="font-medium text-slate-700">Site</span>
                  <select className="rounded-lg border border-slate-300 px-3 py-2" value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="font-medium text-slate-700">Report date</span>
                  <input className="rounded-lg border border-slate-300 px-3 py-2" type="date" value={reportDate} onChange={(event) => setReportDate(event.target.value)} />
                </label>
              </div>
              <label className="mt-4 flex flex-col gap-2 text-sm">
                <span className="font-medium text-slate-700">Report method name</span>
                <input className="rounded-lg border border-slate-300 px-3 py-2" value={reportMethodName} onChange={(event) => setReportMethodName(event.target.value)} />
              </label>
              <label className="mt-4 flex flex-col gap-2 text-sm">
                <span className="font-medium text-slate-700">Run notes</span>
                <input className="rounded-lg border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional" />
              </label>
              <label className="mt-4 flex flex-col gap-2 text-sm">
                <span className="font-medium text-slate-700">Report note</span>
                <textarea className="min-h-[96px] rounded-lg border border-slate-300 px-3 py-2" value={reportMethodNotes} onChange={(event) => setReportMethodNotes(event.target.value)} />
              </label>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Correction factors</h2>
              <div className="mt-4 space-y-4">
                <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-sm">
                  <input type="checkbox" checked={applyClippingCorrection} onChange={(event) => setApplyClippingCorrection(event.target.checked)} />
                  <span>Apply physical AC cap correction</span>
                </label>
                <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-sm">
                  <input type="checkbox" checked={applyCurtailmentCorrection} onChange={(event) => setApplyCurtailmentCorrection(event.target.checked)} />
                  <span>Apply curtailment correction</span>
                </label>
                <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-sm">
                  <input type="checkbox" checked={applyAvailabilityCorrection} onChange={(event) => setApplyAvailabilityCorrection(event.target.checked)} />
                  <span>Apply inverter availability correction</span>
                </label>
                <label className="flex flex-col gap-2 text-sm">
                  <span className="font-medium text-slate-700">Irradiance validity threshold (W/m2)</span>
                  <input
                    className="rounded-lg border border-slate-300 px-3 py-2"
                    type="number"
                    min={0}
                    step="10"
                    value={irradianceThreshold}
                    onChange={(event) => setIrradianceThreshold(Number(event.target.value || 0))}
                  />
                </label>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Pure IEC PR is evaluated on valid minutes with POA irradiance above the selected threshold. Minutes below the threshold are omitted and disclosed in the report.
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                  Inverter availability correction reduces corrected expected energy when part of the inverter fleet is unavailable during valid IEC minutes. Pure IEC PR remains unchanged.
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Configuration Preview</h2>
                <p className="mt-1 text-sm text-slate-500">Sections are derived from exactly one IRRAD and one MODTEMP mapping per array.</p>
              </div>
              {previewLoading ? <div className="text-sm text-slate-500">Loading preview...</div> : null}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Signals</div>
                <div className="mt-3 space-y-2 text-sm text-slate-700">
                  <div>POUTSITE: {displaySignal(preview?.poutsite_source)}</div>
                  <div>SETPOINT: {displaySignal(preview?.setpoint_source)}</div>
                  <div>Timezone: {preview?.site?.timezone || "UTC"}</div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">Validation</div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className={preview?.errors?.length ? "text-rose-700" : "text-emerald-700"}>Errors: {preview?.errors?.length || 0}</div>
                  <div className={preview?.warnings?.length ? "text-amber-700" : "text-emerald-700"}>Warnings: {preview?.warnings?.length || 0}</div>
                  <div className="text-slate-700">Sections: {preview?.sections?.length || 0}</div>
                </div>
              </div>
            </div>

            {preview?.errors?.length ? (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
                <div className="text-sm font-semibold text-rose-900">Configuration Errors</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-800">
                  {preview.errors.map((row, index) => (
                    <li key={`preview-error-${index}`}>{row}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {preview?.warnings?.length ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-sm font-semibold text-amber-900">Warnings</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
                  {preview.warnings.map((row, index) => (
                    <li key={`preview-warning-${index}`}>{row}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="px-3 py-2 font-medium">Section</th>
                    <th className="px-3 py-2 font-medium">IRRAD</th>
                    <th className="px-3 py-2 font-medium">MODTEMP</th>
                    <th className="px-3 py-2 font-medium">Arrays</th>
                    <th className="px-3 py-2 font-medium">DC kWp</th>
                    <th className="px-3 py-2 font-medium">AC kW</th>
                    <th className="px-3 py-2 font-medium">DC wt</th>
                    <th className="px-3 py-2 font-medium">AC wt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {(preview?.sections || []).map((section) => (
                    <tr key={section.section_key}>
                      <td className="px-3 py-2"><ArrayNamesBlock names={section.array_names} /></td>
                      <td className="px-3 py-2 text-slate-600">{displaySignal(section.irradiance_signal_source)}</td>
                      <td className="px-3 py-2 text-slate-600">{displaySignal(section.module_temp_signal_source)}</td>
                      <td className="px-3 py-2 text-slate-600">{section.array_ids?.length || 0}</td>
                      <td className="px-3 py-2 text-slate-600">{numberLabel(section.installed_dc_kwp, 1)}</td>
                      <td className="px-3 py-2 text-slate-600">{numberLabel(section.installed_ac_kw, 1)}</td>
                      <td className="px-3 py-2 text-slate-600">{percent(section.dc_weighting_factor, 2)}</td>
                      <td className="px-3 py-2 text-slate-600">{percent(section.ac_weighting_factor, 2)}</td>
                    </tr>
                  ))}
                  {!preview?.sections?.length ? (
                    <tr>
                      <td className="px-3 py-4 text-slate-500" colSpan={8}>
                        No IEC sections available for the selected site/date.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Instrumentation preview</h3>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-3 py-2 font-medium">Section</th>
                      <th className="px-3 py-2 font-medium">Role</th>
                      <th className="px-3 py-2 font-medium">Sensor</th>
                      <th className="px-3 py-2 font-medium">Make / model</th>
                      <th className="px-3 py-2 font-medium">Certificate</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {instrumentationPreviewRows(preview?.sections).map((row, index) => (
                      <tr key={`preview-instrument-${index}`}>
                        <td className="px-3 py-2 text-slate-700">{row.section_label}</td>
                        <td className="px-3 py-2 text-slate-600">{row.sensor_role}</td>
                        <td className="px-3 py-2 text-slate-600">{displaySensorName(row.sensor)}</td>
                        <td className="px-3 py-2 text-slate-600">{[row.sensor?.manufacturer, row.sensor?.model].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{row.sensor?.calibration_certificate_number || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-2">
                            <InstrumentStatusBadge label={row.sensor?.calibration_status} />
                            <InstrumentStatusBadge label={row.sensor?.certificate_status} />
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!instrumentationPreviewRows(preview?.sections).length ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={6}>
                          No instrumentation rows available for the current section set.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Manual Exclusions</h2>
                <p className="mt-1 text-sm text-slate-500">Add zero or more local-time exclusions inside the selected report day.</p>
              </div>
              <button className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700" onClick={addExclusion}>
                Add exclusion
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {exclusionRows.map((row, index) => (
                <div key={`exclusion-${index}`} className="grid gap-3 rounded-xl border border-slate-200 p-4 lg:grid-cols-6">
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="font-medium text-slate-700">Scope</span>
                    <select
                      className="rounded-lg border border-slate-300 px-3 py-2"
                      value={row.exclusion_scope}
                      onChange={(event) =>
                        updateExclusion(index, {
                          exclusion_scope: event.target.value as "site" | "section",
                          section_key: event.target.value === "site" ? undefined : row.section_key,
                        })
                      }
                    >
                      <option value="site">Site</option>
                      <option value="section">Section</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="font-medium text-slate-700">Start</span>
                    <input className="rounded-lg border border-slate-300 px-3 py-2" type="time" value={row.start_time} onChange={(event) => updateExclusion(index, { start_time: event.target.value })} />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="font-medium text-slate-700">End</span>
                    <input className="rounded-lg border border-slate-300 px-3 py-2" type="time" value={row.end_time} onChange={(event) => updateExclusion(index, { end_time: event.target.value })} />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="font-medium text-slate-700">Type</span>
                    <input className="rounded-lg border border-slate-300 px-3 py-2" value={row.exclusion_type} onChange={(event) => updateExclusion(index, { exclusion_type: event.target.value })} />
                  </label>
                  <label className="flex flex-col gap-2 text-sm">
                    <span className="font-medium text-slate-700">Section</span>
                    <select
                      className="rounded-lg border border-slate-300 px-3 py-2"
                      value={row.section_key || ""}
                      onChange={(event) => updateExclusion(index, { section_key: event.target.value || undefined })}
                      disabled={row.exclusion_scope !== "section"}
                    >
                      <option value="">—</option>
                      {(preview?.sections || []).map((section) => (
                        <option key={section.section_key} value={section.section_key}>
                          {(section.array_names || []).join(", ")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="flex flex-col gap-2">
                    <label className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-slate-700">Reason</span>
                      <input
                        className="rounded-lg border border-slate-300 px-3 py-2"
                        value={row.reason}
                        onChange={(event) => updateExclusion(index, { reason: event.target.value })}
                        placeholder="Grid issue, uncertain data, contract exclusion..."
                      />
                    </label>
                    <button className="text-left text-sm font-medium text-rose-700" onClick={() => removeExclusion(index)}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}

              {!exclusionRows.length ? (
                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">
                  No manual exclusions defined for this run.
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <>
          {runResult?.payload ? (
            <>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Stored report</h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Site {runResult.payload.meta?.site_name || "—"} on {runResult.payload.meta?.report_date || "—"}.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <SectionBadge>Run ID: {runResult.id}</SectionBadge>
                    <SectionBadge>Status: {runResult.status}</SectionBadge>
                    <SectionBadge>Type: {runResult.run_type}</SectionBadge>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-7">
                <SummaryCard label="Grid availability observed" value={percent(mainResults?.grid_availability_observed_pct)} />
                <SummaryCard label="Pure IEC PR" value={percent(mainResults?.pure_iec_pr)} tone={toneClass(mainResults?.pure_iec_pr)} />
                <SummaryCard label="Corrected PR" value={percent(mainResults?.corrected_pr)} tone={toneClass(mainResults?.corrected_pr)} />
                <SummaryCard label="Full-Day POI Energy" value={energyLabel(fullDayPoiEnergyWh)} />
                <SummaryCard label="Measured Energy (Evaluated Minutes)" value={energyLabel(measuredEnergyEvaluatedWh)} />
                <SummaryCard label="Availability on Evaluated Minutes" value={percent(mainResults?.availability_avg_pct_evaluated_minutes)} />
                <SummaryCard label="Evaluated minutes" value={String(summary?.evaluated_minutes ?? "—")} hint={`Threshold omissions: ${summary?.below_irradiance_threshold_minutes ?? "—"}`} />
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900">Report notes</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <SectionBadge>POUTSITE: {displaySignal(runResult.payload.signals?.poutsite)}</SectionBadge>
                    <SectionBadge>SETPOINT: {displaySignal(runResult.payload.signals?.setpoint)}</SectionBadge>
                    <SectionBadge>Threshold: {numberLabel(runResult.payload.meta?.irradiance_threshold_wm2, 0)} W/m2</SectionBadge>
                    <SectionBadge>Timezone: {runResult.payload.meta?.timezone || "UTC"}</SectionBadge>
                    <SectionBadge>Clip: {runResult.payload.meta?.apply_clipping_correction ? "On" : "Off"}</SectionBadge>
                    <SectionBadge>Curtail: {runResult.payload.meta?.apply_curtailment_correction ? "On" : "Off"}</SectionBadge>
                    <SectionBadge>Availability: {runResult.payload.meta?.apply_availability_correction ? "On" : "Off"}</SectionBadge>
                  </div>
                  {runResult.payload.meta?.contract_method_notes ? (
                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      {runResult.payload.meta.contract_method_notes}
                    </div>
                  ) : null}
                  <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-slate-700">
                    {(runResult.payload.notes_block || []).map((note, index) => (
                      <li key={`note-${index}`}>{note}</li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900">Polling and quality summary</h2>
                  <div className="mt-4 space-y-4 text-sm">
                    <div>
                      <div className="font-medium text-slate-800">Polling events</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(runResult.payload.polling || {}).map(([key, value]) => (
                          <SectionBadge key={`poll-${key}`}>
                            {key}: {value}
                          </SectionBadge>
                        ))}
                        {!Object.keys(runResult.payload.polling || {}).length ? <SectionBadge>No poll events in window</SectionBadge> : null}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">Stale-but-included samples</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(summary?.stale_filled_minutes_by_signal || {}).map(([key, value]) => (
                          <SectionBadge key={`stale-${key}`}>
                            {key}: {value}
                          </SectionBadge>
                        ))}
                        {!Object.keys(summary?.stale_filled_minutes_by_signal || {}).length ? <SectionBadge>None</SectionBadge> : null}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">Excluded minutes by reason</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {Object.entries(summary?.excluded_minutes_by_reason || {}).map(([key, value]) => (
                          <SectionBadge key={`excluded-${key}`}>
                            {key}: {value}
                          </SectionBadge>
                        ))}
                        {!Object.keys(summary?.excluded_minutes_by_reason || {}).length ? <SectionBadge>None</SectionBadge> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Instrumentation and calibration</h2>
                <p className="mt-1 text-sm text-slate-500">Calibration metadata is frozen into the run payload for auditability.</p>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Section</th>
                        <th className="px-3 py-2 font-medium">Role</th>
                        <th className="px-3 py-2 font-medium">Sensor</th>
                        <th className="px-3 py-2 font-medium">Make / model</th>
                        <th className="px-3 py-2 font-medium">Serial</th>
                        <th className="px-3 py-2 font-medium">Certificate</th>
                        <th className="px-3 py-2 font-medium">Calibration date</th>
                        <th className="px-3 py-2 font-medium">Valid until</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {(runResult.payload.instrumentation || []).map((item, index) => (
                        <tr key={`instrumentation-row-${index}`}>
                          <td className="px-3 py-2 text-slate-700">{String(item.section_label || "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{String(item.sensor_role || item.signal_type_short || "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{displaySensorName(item)}</td>
                          <td className="px-3 py-2 text-slate-600">{[item.manufacturer, item.model].filter(Boolean).join(" / ") || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{item.sensor_serial_number || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{item.calibration_certificate_number || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{item.calibration_date || "—"}</td>
                          <td className="px-3 py-2 text-slate-600">{item.calibration_valid_until || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <InstrumentStatusBadge label={item.calibration_status} />
                              <InstrumentStatusBadge label={item.certificate_status} />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!runResult.payload.instrumentation?.length ? (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={9}>
                            No instrumentation metadata was stored for this run.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Section results</h2>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Section</th>
                        <th className="px-3 py-2 font-medium">IRRAD</th>
                        <th className="px-3 py-2 font-medium">MODTEMP</th>
                        <th className="px-3 py-2 font-medium">Arrays</th>
                        <th className="px-3 py-2 font-medium">DC kWp</th>
                        <th className="px-3 py-2 font-medium">AC kW</th>
                        <th className="px-3 py-2 font-medium">DC wt</th>
                        <th className="px-3 py-2 font-medium">AC wt</th>
                        <th className="px-3 py-2 font-medium">Availability Avg</th>
                        <th className="px-3 py-2 font-medium">Expected IEC</th>
                        <th className="px-3 py-2 font-medium">Expected Corrected</th>
                        <th className="px-3 py-2 font-medium">Valid Min</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {(runResult.payload.sections || []).map((section) => {
                        const previewSection = (preview?.sections || []).find((row) => row.section_key === section.section_key);
                        return (
                          <tr key={section.section_key}>
                            <td className="px-3 py-2"><ArrayNamesBlock names={section.array_names} /></td>
                            <td className="px-3 py-2 text-slate-600">{displaySignal(previewSection?.irradiance_signal_source)}</td>
                            <td className="px-3 py-2 text-slate-600">{displaySignal(previewSection?.module_temp_signal_source)}</td>
                            <td className="px-3 py-2 text-slate-600">{section.array_ids?.length || 0}</td>
                            <td className="px-3 py-2 text-slate-600">{numberLabel(section.installed_dc_kwp, 1)}</td>
                            <td className="px-3 py-2 text-slate-600">{numberLabel(section.installed_ac_kw, 1)}</td>
                            <td className="px-3 py-2 text-slate-600">{percent(section.dc_weighting_factor, 2)}</td>
                            <td className="px-3 py-2 text-slate-600">{percent(section.ac_weighting_factor, 2)}</td>
                            <td className="px-3 py-2 text-slate-600">{percent(section.availability_avg_pct, 2)}</td>
                            <td className="px-3 py-2 text-slate-600">{energyLabel(section.expected_iec_wh)}</td>
                            <td className="px-3 py-2 text-slate-600">{energyLabel(section.expected_corrected_wh)}</td>
                            <td className="px-3 py-2 text-slate-600">{section.valid_minutes ?? "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Graphic summary</h2>
                <p className="mt-1 text-sm text-slate-500">POA irradiance, POUTSITE, and module temperature on the evaluated day with tracker-style zoom controls.</p>
                <div className="mt-4">
                  <GraphicSummaryChart items={runResult.payload.graphic_summary?.items || []} />
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Applied exclusions</h2>
                <div className="mt-4 space-y-3">
                  {runExclusions.length ? (
                    runExclusions.map((row) => (
                      <div key={`run-exclusion-${row.id}`} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
                        <div className="font-medium text-slate-900">{row.exclusion_scope === "section" ? `Section ${row.section_key}` : "Site-wide exclusion"}</div>
                        <div className="mt-1 text-slate-600">
                          {row.start_utc} to {row.end_utc}
                        </div>
                        <div className="mt-1 text-slate-700">{row.reason}</div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500">No exclusions were applied in this run.</div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900">Minute-level export preview</h2>
                <div className="mt-1 flex flex-col gap-3 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    Showing rows {minuteRowStart || 0} to {minuteRowEnd || 0} of {minuteRows?.total || 0}.
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Rows per page</span>
                    <select
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
                      value={minutePageSize}
                      onChange={async (event) => {
                        const nextPageSize = Number(event.target.value);
                        setMinutePageSize(nextPageSize);
                        if (runResult?.id) {
                          await loadMinuteRows(runResult.id, 0, nextPageSize);
                        }
                      }}
                    >
                      {[100, 250, 500].map((size) => (
                        <option key={`page-size-${size}`} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                    <button
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100"
                      disabled={!runResult?.id || minutePage <= 0 || minuteLoading}
                      onClick={() => runResult?.id && loadMinuteRows(runResult.id, minutePage - 1, minutePageSize)}
                    >
                      Previous
                    </button>
                    <div className="min-w-[88px] text-center text-slate-600">
                      Page {minutePage + 1} / {minuteTotalPages}
                    </div>
                    <button
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:bg-slate-100"
                      disabled={!runResult?.id || minutePage + 1 >= minuteTotalPages || minuteLoading}
                      onClick={() => runResult?.id && loadMinuteRows(runResult.id, minutePage + 1, minutePageSize)}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50 text-left text-slate-600">
                      <tr>
                        <th className="px-3 py-2 font-medium">Minute UTC</th>
                        <th className="px-3 py-2 font-medium">Section</th>
                        <th className="px-3 py-2 font-medium">Section POI MW</th>
                        <th className="px-3 py-2 font-medium">Site POUTSITE MW</th>
                        <th className="px-3 py-2 font-medium">POA</th>
                        <th className="px-3 py-2 font-medium">Mod Temp</th>
                        <th className="px-3 py-2 font-medium">Expected IEC Wh</th>
                        <th className="px-3 py-2 font-medium">Expected Corr Wh</th>
                        <th className="px-3 py-2 font-medium">Site Avail</th>
                        <th className="px-3 py-2 font-medium">Section Avail</th>
                        <th className="px-3 py-2 font-medium">POI Valid</th>
                        <th className="px-3 py-2 font-medium">Section Valid</th>
                        <th className="px-3 py-2 font-medium">AC wt</th>
                        <th className="px-3 py-2 font-medium">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {(minuteRows?.items || []).map((row, index) => (
                        <tr key={`minute-row-${index}`}>
                          <td className="px-3 py-2 text-slate-600">{String(row.timestamp_minute_utc || "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{String(row.section_label || row.section_key || "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{mwLabel(row.section_measured_poutsite_power_w as number, 3)}</td>
                          <td className="px-3 py-2 text-slate-600">{mwLabel(row.site_measured_poutsite_power_w as number, 3)}</td>
                          <td className="px-3 py-2 text-slate-600">{numberLabel(row.poa_irradiance_wm2 as number, 1)}</td>
                          <td className="px-3 py-2 text-slate-600">{numberLabel(row.module_temp_c as number, 1)}</td>
                          <td className="px-3 py-2 text-slate-600">{numberLabel(row.expected_energy_iec_wh as number, 1)}</td>
                          <td className="px-3 py-2 text-slate-600">{numberLabel(row.expected_energy_corrected_wh as number, 1)}</td>
                          <td className="px-3 py-2 text-slate-600">{percent(row.site_availability_factor as number, 2)}</td>
                          <td className="px-3 py-2 text-slate-600">{percent(row.section_availability_factor as number, 2)}</td>
                          <td className="px-3 py-2 text-slate-600">{String(row.is_poi_valid ?? "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{String(row.is_section_valid ?? "—")}</td>
                          <td className="px-3 py-2 text-slate-600">{percent(row.ac_weighting_factor as number, 2)}</td>
                          <td className="px-3 py-2 text-slate-600">
                            {[
                              row.is_below_irradiance_threshold ? "below-threshold" : null,
                              row.is_stale_poutsite ? "stale-poutsite" : null,
                              row.is_stale_irrad ? "stale-irrad" : null,
                              row.is_stale_modtemp ? "stale-modtemp" : null,
                              row.is_ac_capped ? "ac-cap" : null,
                              row.is_curtailed ? "curtailed" : null,
                              row.is_excluded_manual ? "manual-excl" : null,
                            ]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </td>
                        </tr>
                      ))}
                      {!minuteRows?.items?.length ? (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={14}>
                            {minuteLoading ? "Loading minute rows..." : "No minute rows loaded yet."}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-sm text-slate-500 shadow-sm">
              No report selected yet. Run a new IEC PR evaluation or open an existing stored run.
            </div>
          )}
        </>
      )}

      <Dialog open={storedReportsOpen} onOpenChange={setStoredReportsOpen}>
        <DialogContent className="max-h-[85vh] w-[min(1200px,96vw)] max-w-none overflow-hidden">
          <DialogHeader>
            <DialogTitle>Stored reports</DialogTitle>
            <div className="mt-1 text-sm text-slate-500">
              All stored IEC PR runs for {sites.find((site) => String(site.id) === siteId)?.name || "the selected site"}.
            </div>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-4 text-sm text-slate-500">
            <div>Open any stored run directly from here. The selected report date is {reportDate || "—"}.</div>
            {historyLoading ? <div>Loading history...</div> : null}
          </div>
          <div className="mt-4 max-h-[60vh] overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Report date</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Threshold</th>
                  <th className="px-3 py-2 font-medium">Clip</th>
                  <th className="px-3 py-2 font-medium">Curtail</th>
                  <th className="px-3 py-2 font-medium">Availability</th>
                  <th className="px-3 py-2 font-medium">Full-Day POI</th>
                  <th className="px-3 py-2 font-medium">Measured Eval</th>
                  <th className="px-3 py-2 font-medium">Pure IEC PR</th>
                  <th className="px-3 py-2 font-medium">Corrected PR</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {existingRuns.map((run) => (
                  <tr key={`stored-run-${run.id}`}>
                    <td className="px-3 py-2 text-slate-600">{run.report_date || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{run.created_at || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{run.status}</td>
                    <td className="px-3 py-2 text-slate-600">{numberLabel(run.irradiance_threshold_wm2, 0)} W/m2</td>
                    <td className="px-3 py-2 text-slate-600">{run.apply_clipping_correction ? "Yes" : "No"}</td>
                    <td className="px-3 py-2 text-slate-600">{run.apply_curtailment_correction ? "Yes" : "No"}</td>
                    <td className="px-3 py-2 text-slate-600">{run.apply_availability_correction ? "Yes" : "No"}</td>
                    <td className="px-3 py-2 text-slate-600">{energyLabel(run.total_full_day_poi_energy_wh)}</td>
                    <td className="px-3 py-2 text-slate-600">{energyLabel(run.total_measured_energy_evaluated_wh)}</td>
                    <td className={`px-3 py-2 ${toneClass(run.pr_iec_raw)}`}>{percent(run.pr_iec_raw)}</td>
                    <td className={`px-3 py-2 ${toneClass(run.pr_corrected)}`}>{percent(run.pr_corrected)}</td>
                    <td className="px-3 py-2">
                      <button
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
                        onClick={() => loadRunDetails(run.id)}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
                {!existingRuns.length ? (
                  <tr>
                    <td className="px-3 py-4 text-slate-500" colSpan={12}>
                      No stored IEC PR runs found for the selected site.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IecPrReportsPage;
